import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { BloomreachProductType } from './use-search-types';
import { useSearch } from './useSearch';
import type { UseSearchResult } from './use-search-types';

interface UseSearches {
    currentProductType: BloomreachProductType;
    searchesToShow: UseSearchResult[];
    searchByType: Record<BloomreachProductType, UseSearchResult>;
    currentSearch: UseSearchResult;
    hardgoodSearch: UseSearchResult;
    culinarySearch: UseSearchResult;
    recipeSearch: UseSearchResult;
    totalResults: number;
    isLoading: boolean;
    isFetching: boolean;
    isHardgoodActive: boolean;
    onChangeQuery: (query: string) => void;
}

export const useSearches = (): UseSearches => {
    const location = useLocation();

    const hardgoodSearch = useSearch(BloomreachProductType.hardgood);
    const culinarySearch = useSearch(BloomreachProductType.culinary);
    const recipeSearch = useSearch(BloomreachProductType.recipe);

    return useMemo<UseSearches>(() => {
        const searches = [hardgoodSearch, culinarySearch, recipeSearch];

        const searchByType: Record<BloomreachProductType, UseSearchResult> = {
            [BloomreachProductType.hardgood]: hardgoodSearch,
            [BloomreachProductType.recipe]: recipeSearch,
            [BloomreachProductType.culinary]: culinarySearch,
        };

        const onChangeQuery = (query: string): void =>
            searches.forEach((search) =>
                search.onSearchEvent(query, search.productType === BloomreachProductType.hardgood),
            );

        const currentProductType =
            (new URLSearchParams(location.search).get('productType') as BloomreachProductType) ??
            BloomreachProductType.hardgood;

        const currentSearch = searchByType[currentProductType];

        return {
            currentProductType,
            searchesToShow: searches,
            searchByType,
            currentSearch,
            hardgoodSearch,
            culinarySearch,
            recipeSearch,
            totalResults: searches.reduce((acc, { total }) => acc + (total ?? 0), 0),
            isLoading: currentSearch.isLoading,
            isFetching: !currentSearch.isLoading && currentSearch.isFetching,
            isHardgoodActive: currentProductType === BloomreachProductType.hardgood,
            onChangeQuery,
        };
    }, [hardgoodSearch, culinarySearch, recipeSearch, location]);
};
